import React, { useEffect, useState, useRef } from "react";
import { Breadcrumb, Button, Modal, Spin } from "antd";
import { useRouter, withRouter } from "next/router";
import Link from "next/link";
import styles from "@aspen/theme/Wallet.module.less";
import {
  IBindingBusinessType,
  IBusinessVerificationHeaderModel,
  IReqSecurityVerifyHeader,
  IUserEventEnums,
  type IInjectProps,
  ISubAccountAuthType,
} from "@aspen/model";
import { availableSpots, submitFiatWithdraw, userEventCheck } from "@aspen/services";
import {
  BENCHMARK_CURRENCY,
  hasPermissionOrNotSubAccount,
  i18nUtil,
  WALLET_PATHS,
} from "@aspen/libs";
import { message, ModalBase } from "@aspen/ui";
import { History, ModalBusinessVerification, WithRiskReminder } from "@aspen/widgets";
import type { FiatWithdrawalFormRef } from "@adgm/cross-ui";
import { FiatWithdrawalForm } from "@adgm/cross-ui";

interface IProps extends IInjectProps {
  currency: string;
}

const WithdrawFiat: React.FC<IProps> = (props: IProps) => {
  const intl = i18nUtil.t();
  const [loading, setLoading] = useState(false);
  const [showVerifyTipModal, setShowVerifyTipModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [showBusinessVerification, setShowBusinessVerification] = useState(false);
  const [verifyTip, setVerifyTip] = useState("");
  const currency = (props.router?.query?.currency as string) ?? BENCHMARK_CURRENCY;
  const formRef = useRef<FiatWithdrawalFormRef>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const router = useRouter();
  const [currencyBalance, setCurrencyBalance] = useState(0);

  useEffect(() => {
    checkWithdrawalEligibility();
    getCurrencyBalance();
  }, []);

  const handleFormSubmit = (verifyParam: IBusinessVerificationHeaderModel) => {
    if (formRef.current) {
      const formSubmitRes = formRef.current?.getValidatedData() || {};
      setLoading(true);
      const {
        withdrawalAmount,
        bankName,
        swiftBicCode,
        bankCode,
        bankBranchCode,
        bankAddress,
        bankAccountName,
        bankAccountNumber,
      } = formSubmitRes;
      let headerParams: IReqSecurityVerifyHeader = {
        businessType: IBindingBusinessType.applyWithdraw,
        smsType: 0,
      };
      headerParams = { ...headerParams, ...verifyParam };
      submitFiatWithdraw(
        {
          currency,
          amount: Number(withdrawalAmount),
          bankName,
          swiftBicCode,
          bankCode,
          bankBranchCode,
          bankAddress,
          bankAccountName,
          bankAccountNumber,
        },
        headerParams,
      )
        .then(res => {
          if (res?.code == "0") {
            setShowBusinessVerification(false);
            setShowResultModal(true);
          } else {
            message.error(res?.msg || intl["wallet.withdraw.failed.tips"]);
          }
        })
        .catch(err => {
          message.error(intl["wallet.withdraw.failed.tips"]);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };
  // 获取法币余额
  const getCurrencyBalance = () => {
    setLoading(true);
    availableSpots({ currency })
      .then(res => {
        if (res?.code == "0") {
          setCurrencyBalance(res?.data?.[currency]);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // 安全检查，判断当前用户是否可以进行withdrawal操作
  const checkWithdrawalEligibility = () => {
    setLoading(true);
    userEventCheck({ userEventEnum: IUserEventEnums.ApplyWithdraw })
      .then(res => {
        if (res?.code !== "0") {
          const errors = [
            "customer.not.allowed.withdraw.after.modify.password",
            "customer.not.allowed.withdraw.after.modify.bind",
          ];
          if (errors.includes(res?.msg)) {
            setShowVerifyTipModal(true);
            setVerifyTip(res?.msg);
          }
        }
      })
      .catch(() => {
        setShowVerifyTipModal(true);
        setVerifyTip("Network error");
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const handleVerifyModal = () => {
    setShowVerifyTipModal(false);
    props.router.back();
  };

  return (
    <section className={styles.withdraw}>
      <Spin spinning={loading}>
        <Breadcrumb>
          <Breadcrumb.Item>{intl["kyc.certify.location"]}</Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link href={WALLET_PATHS.WALLET}>{intl["wallet"]}</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            {`${intl["router.title.wallet.withdraw.fiat"]}-${currency}`}
          </Breadcrumb.Item>
        </Breadcrumb>

        <div className={styles.withdrawContent}>
          <p className={styles.title}>{intl["router.title.wallet.withdraw.fiat"]}</p>
          <FiatWithdrawalForm
            currency={currency}
            currencyBalance={currencyBalance}
            ref={formRef}
            onValidationChange={isValid => {
              setIsFormValid(isValid);
            }}
          />
          <div>
            <Button
              onClick={() => {
                router.back();
              }}
              style={{ width: 170, marginRight: 20, height: 40 }}>
              {intl["button.back"]}
            </Button>
            {hasPermissionOrNotSubAccount(ISubAccountAuthType.WITHDRAW) ? (
              <Button
                type="primary"
                onClick={() => setShowBusinessVerification(true)}
                style={{ width: 170, height: 40 }}
                disabled={!isFormValid}>
                {intl["button.submit"]}
              </Button>
            ) : null}
          </div>
        </div>

        <History type={2} currency={currency} fiat={true} showViewAll />
      </Spin>
      {showVerifyTipModal ? (
        <ModalBase
          buttonType="only"
          open={showVerifyTipModal}
          title={intl["modal.confirm.title"]}
          onOk={handleVerifyModal}
          okText={intl["button.confirm"]}
          onCancel={handleVerifyModal}>
          <p className={styles.verifyTipStyle}>{intl[verifyTip]}</p>
        </ModalBase>
      ) : null}
      {showBusinessVerification ? (
        <ModalBusinessVerification
          confirmLoading={loading}
          visible={showBusinessVerification}
          businessType={IBindingBusinessType.applyWithdraw}
          confirm={res => {
            handleFormSubmit(res);
          }}
          cancel={() => {
            setShowBusinessVerification(false);
          }}
        />
      ) : null}
      {showResultModal ? (
        <Modal
          wrapClassName={styles.modal}
          title={intl["wallet.withdraw.fiat.success.title"]}
          open={showResultModal}
          centered
          footer={null}
          width={480}
          onCancel={() => {
            setShowResultModal(false);
            props.router.back();
          }}>
          <div>
            <p>{intl["wallet.withdraw.fiat.success.tips"]}</p>
            <div className={styles.confirmRow}>
              <Button
                type="primary"
                onClick={() => {
                  setShowResultModal(false);
                  props.router.back();
                }}>
                {intl["button.ok"]}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </section>
  );
};

export const PageWithdrawFiat = WithRiskReminder(withRouter(WithdrawFiat));
